import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  mergeClasses,
  makeStyles,
  tokens,
  Card,
  Text,
} from '@fluentui/react-components';
import _ from 'underscore';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { ApplyButton, SearchableSelector } from '@bingads-webui-react/copilot-ui-base';
import { DateSelector } from './date-selector';

export type CampaignSaveData = {
  selectedCampaign?: { name: string, id: string };
  startDate?: Date;
  endDate?: Date;
};

interface CampaignAndDateSelectorProps {
  getCampaigns?: () => Promise<Array<{ name: string, id: string }>>;
  onSave: (_event: any, data?:CampaignSaveData) => void;
  showCampaignSelector: boolean;
  showDateSelector: boolean;
  cardText?: string;
}

export const useCampaignAndDateSelectorStyles = makeStyles({
  card: {
    'border-radius': `${tokens.borderRadiusLarge} !important`,
    rowGap: tokens.spacingVerticalS,
    minWidth: '272px',
    width: '100%',
    '&.campaign-and-date-selector': {
      marginTop: tokens.spacingVerticalM,
      display: 'inline-block',
    },
  },
});

export const CampaignAndDateSelector: React.FC<CampaignAndDateSelectorProps> = ({
  getCampaigns,
  onSave,
  showCampaignSelector,
  showDateSelector,
  cardText,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const styles = useCampaignAndDateSelectorStyles();
  const [campaigns, setCampaigns] = useState<Array<{ name: string, id: string }>>();
  const [selectedCampaign, setSelectedCampaign] = useState<{ name: string, id: string }>();
  const today = new Date();
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(today);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(lastWeek);
  const [disabled, setDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleApplyClick = useCallback((e: any) => {
    const data = { selectedCampaign, startDate: selectedStartDate, endDate: selectedEndDate };
    if (!selectedCampaign) {
      return setErrorMessage(i18n?.getString(_TL_('This field is required')));
    }
    onSave(e, data);
    return setDisabled(true);
  }, [onSave, selectedCampaign, selectedStartDate, selectedEndDate, setDisabled]);

  const onSelectCampaign = useCallback((_event:any, data:any) => {
    setErrorMessage('');

    const id = data?.optionValue;
    const name = data?.optionText;
    if (id && name) {
      setSelectedCampaign({ id, name });
    } else {
      // If the selected campaign is empty, reset the selected campaign to disable the apply button
      setSelectedCampaign(undefined);
    }
  }, []);

  let defaultCardText;
  if (showCampaignSelector && showDateSelector) {
    defaultCardText = _TL_('Please confirm the campaign name and date range to generate a summary.');
  } else if (showDateSelector) {
    defaultCardText = _TL_('Please confirm the date range so I can summarize for you.');
  } else {
    defaultCardText = _TL_('Please confirm the campaign name so I can summarize for you.');
  }

  const resetCampaign = useCallback(() => {
    setSelectedCampaign(undefined);
  }, [setSelectedCampaign]);

  useEffect(() => {
    if (_.isFunction(getCampaigns)) {
      getCampaigns().then((data: Array<{ name: string, id: string }> | undefined) => {
        resetCampaign();
        setCampaigns(data);
      });
    }
  }, []);

  return (
    <Card size="large" className={mergeClasses('campaign-and-date-selector', styles.card)}>
      <Text size={300}>
        {i18n?.getString(cardText || defaultCardText)}
      </Text>
      {((showCampaignSelector || !showDateSelector) && campaigns)
      && (
      <SearchableSelector
        entities={campaigns}
        onSelectEntity={onSelectCampaign}
        label={i18n?.getString(_TL_('Campaign name'))}
        placeholder={i18n?.getString(_TL_('Search campaign name'))}
        errorMessage={errorMessage}
      />
      )}
      {(showDateSelector)
      && (
      <DateSelector
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
      />
      )}
      <ApplyButton
        onSave={handleApplyClick}
        disabled={disabled}
      />
    </Card>
  );
};
